import { CMS_TAG_MANAGER_LOCALES } from './_generated/cms-analytics';

/**
 * Whether measurement is switched on for this page's market: the CMS feature flag
 * `tag_manager`, turned on for that market in the admin (DIT-517).
 *
 * Off unless it is on — the opposite of the site's kill-switches, which leave a
 * surface live until a flag says otherwise — because measurement is the step the
 * client put last: after the reviewed banner and privacy policy are live. It gates
 * everything this site keeps or sends for measurement, not only the container: the
 * attribution record, the funnel's events and their once-only keys. Attribution
 * kept without the container is still measurement — the platform reports the first
 * payment to Google from it — so a market that has not been switched on keeps none.
 *
 * The locale comes from the page's path, the same source of truth the cookie banner
 * uses; the build bakes the locales of every market whose flag is on, and turning
 * the flag on rebuilds the site like any CMS publish.
 *
 * **And only in a build that has a container id.** The flag is the client's; the id
 * and the platform's GA4 keys are ours, set in the build and the server secret. A flag
 * switched on before them would keep the record and push the events with nothing to
 * receive either — a half-measurement nobody chose — so a build without the id
 * measures nothing, wherever the flag stands.
 */
export function measurementOn(
  locale: string = localeFromPath(),
  enabledLocales: readonly string[] = CMS_TAG_MANAGER_LOCALES,
  id: string = containerId(),
): boolean {
  return id !== '' && enabledLocales.includes(locale);
}

/** The container id, from the environment. Empty disables the whole thing. */
export function containerId(): string {
  return (import.meta.env.VITE_GTM_CONTAINER_ID || '').trim();
}

function localeFromPath(): string {
  if (typeof window === 'undefined') return '';
  return (window.location?.pathname ?? '').split('/').filter(Boolean)[0] ?? '';
}
